import {
    Layout,
    Card,
    Stack,
    Button,
    TextField,
    FormLayout,
    RadioButton,
    ButtonGroup,
    InlineError
} from "@shopify/polaris";
import { ReactElement, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { kmToMiles, milesToKm } from "../../scripts/UnitsConverter";
import { updateCarbonSectionData } from "../../store/actions/CarbonSection";
import { CarbonSectionInputData } from "../../types/CarbonSection";
import {
    CarbonSectionType,
    ICarbonSection
} from "../../types/CarbonSectionType";
import { PortalState } from "../../types/PortalState";
import styles from "./CarbonSection.module.css";

const UNITS = {
    KM: "km",
    MI: "miles"
};

export default function CarbonSectionInput({
    type,
    data,
    description,
    title,
    inputLabel
}: ICarbonSection): ReactElement {
    const { t } = useTranslation("common");

    const dispatch = useDispatch();

    const [inputUnit, setInputUnit] = useState(
        data.Unit ? (data.Unit === UNITS.KM ? UNITS.KM : UNITS.MI) : UNITS.KM
    );
    const [radioError, setRadioError] = useState("");
    const [inputError, setInputError] = useState("");
    const [loading, setLoading] = useState(false);
    const [inputValue, setInputValue] = useState(getInitialValue());

    function getInitialValue(): string {
        if (data.Value) {
            if (data.Unit === UNITS.MI) {
                return kmToMiles(data.Value);
            }
            return data.Value;
        }
        return "";
    }

    const handleChange = useCallback((newValue) => setInputValue(newValue), []);

    const [carFuelType, setCarFuelType] = useState(
        data.FuelType ? data.FuelType : null
    );

    const handleCarFuelType = useCallback(
        (_checked, newValue) => setCarFuelType(newValue),
        []
    );

    const currentInspection = useSelector(
        (state: PortalState) => state.portal.activeInspection
    );
    const sectionComplete: boolean = useSelector(
        (state: any) =>
            state.portal.activeInspection.SurveyorCompletionStatus
                .CarbonSectionComplete
    );

    const FUEL_TYPES = {
        PETROL: {
            name: t("inspection.carbon.petrol"),
            id: t("inspection.carbon.petrol").toLowerCase()
        },
        DIESEL: {
            name: t("inspection.carbon.diesel"),
            id: t("inspection.carbon.diesel").toLowerCase()
        },
        HYBRID: {
            name: t("inspection.carbon.hybrid"),
            id: t("inspection.carbon.hybrid").toLowerCase()
        },
        ELECTRIC: {
            name: t("inspection.carbon.electric"),
            id: t("inspection.carbon.electric").toLowerCase()
        }
    };

    // Saves carbon sections
    async function save() {
        setLoading(true);
        if (inputValue.length > 0) {
            setInputError("");
            if (parseInt(inputValue) < 0) {
                setInputError(t("inspection.carbon.value_0_error"));
                setLoading(false);
                return;
            }
            setLoading(true);
            if (type === CarbonSectionType.CAR && carFuelType == null) {
                setRadioError(t("inspection.carbon.fuel_type_error"));
                setLoading(false);
            } else {
                setRadioError("");
                const request = {
                    FuelType: carFuelType,
                    Type: CarbonSectionType[type].toLowerCase(),
                    Unit: inputUnit,
                    Value: inputUnit === UNITS.MI ? milesToKm(inputValue) : inputValue,
                    Complete: true,
                    InspectionId: currentInspection?.Id.toLowerCase()
                } as CarbonSectionInputData;

                if (currentInspection) {
                    const carbonData = currentInspection.CarbonSectionData;

                    carbonData.inspectionId = currentInspection?.Id;

                    if (type === CarbonSectionType.CAR) {
                        carbonData.CarSection = request;
                    } else if (type === CarbonSectionType.TRAIN) {
                        carbonData.TrainSection = request;
                    } else if (type === CarbonSectionType.AEROPLANE) {
                        carbonData.AeroplaneSection = request;
                    }

                    await updateCarbonSectionData(carbonData, currentInspection, dispatch);

                    setLoading(false);
                }
            }
        } else {
            setInputError(t("inspection.carbon.input_error"));
            setLoading(false);
        }
    }


    return (
        <article className={styles.section}>
            <Layout>
                <Layout.AnnotatedSection
                    title={title}
                    description={description}
                >
                    <Card sectioned>
                        <FormLayout>
                            <TextField
                                label={inputLabel}
                                type="number"
                                min={0}
                                value={inputValue}
                                onChange={handleChange}
                                autoComplete="off"
                                suffix={inputUnit}
                                error={inputError}
                                connectedRight={
                                    <ButtonGroup segmented>
                                        <Button
                                            primary={
                                                inputUnit === UNITS.KM
                                                    ? true
                                                    : false
                                            }
                                            onClick={() => {
                                                setInputUnit(UNITS.KM);
                                            }}
                                        >
                                            km
                                        </Button>
                                        <Button
                                            primary={
                                                inputUnit === UNITS.MI
                                                    ? true
                                                    : false
                                            }
                                            onClick={() => {
                                                setInputUnit(UNITS.MI);
                                            }}
                                        >
                                            miles
                                        </Button>
                                    </ButtonGroup>
                                }
                            />
                        </FormLayout>

                        <div className={styles.space}></div>

                        {type === CarbonSectionType.CAR ? (
                            <>
                                <Stack spacing="extraLoose">
                                    <RadioButton
                                        label={FUEL_TYPES.PETROL.name}
                                        checked={
                                            carFuelType === FUEL_TYPES.PETROL.id
                                        }
                                        id={FUEL_TYPES.PETROL.id}
                                        name={FUEL_TYPES.PETROL.name}
                                        onChange={handleCarFuelType}
                                    />
                                    <RadioButton
                                        label={FUEL_TYPES.DIESEL.name}
                                        checked={
                                            carFuelType === FUEL_TYPES.DIESEL.id
                                        }
                                        id={FUEL_TYPES.DIESEL.id}
                                        name={FUEL_TYPES.DIESEL.name}
                                        onChange={handleCarFuelType}
                                    />
                                    <RadioButton
                                        label={FUEL_TYPES.HYBRID.name}
                                        checked={
                                            carFuelType === FUEL_TYPES.HYBRID.id
                                        }
                                        id={FUEL_TYPES.HYBRID.id}
                                        name={FUEL_TYPES.HYBRID.name}
                                        onChange={handleCarFuelType}
                                    />
                                    <RadioButton
                                        label={FUEL_TYPES.ELECTRIC.name}
                                        checked={
                                            carFuelType ===
                                            FUEL_TYPES.ELECTRIC.id
                                        }
                                        id={FUEL_TYPES.ELECTRIC.id}
                                        name={FUEL_TYPES.ELECTRIC.name}
                                        onChange={handleCarFuelType}
                                    />
                                </Stack>
                                {radioError !== "" ? (
                                    <div className={styles.radioError}>
                                        <InlineError
                                            message={radioError}
                                            fieldID="errorField"
                                        />
                                    </div>
                                ) : null}
                            </>
                        ) : null}

                        <div className={styles.largeSpace}></div>

                        {!sectionComplete ?
                            <Stack alignment="center" distribution="center">
                                <Button
                                    onClick={save}
                                    size="large"
                                    primary
                                    loading={loading}
                                    submit
                                >
                                    {t("inspection.carbon.save")}
                                </Button>
                            </Stack>
                            : null}
                    </Card>
                </Layout.AnnotatedSection>
            </Layout>
        </article>
    );
}
